import React, { useState } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import useCopyWithFeedback from "../../hooks/useCopyWithFeedback";

const EmbedDocumentationPage = () => {
  const BACKEND_URL = process.env.REACT_APP_API_URL || "";
  const EMBED_SERVICE_URL = "https://emotii-embedded.onrender.com";

  const authEndpoint = `${BACKEND_URL.replace(/\/?$/, "/")}api/v1/connector/embedded/auth`;
  const translateEndpoint = `${EMBED_SERVICE_URL}/translate_batch`;

  const [activeInstallTab, setActiveInstallTab] = useState("installation");
  const [activeAuthTab, setActiveAuthTab] = useState("authRequest");
  const [activeTranslateTab, setActiveTranslateTab] = useState("translateRequest");
  const { copied, handleCopy } = useCopyWithFeedback();

  const installationCode = `<script
  src="https://emotii.ai/emotii.ai.embedded.js"
  data-emotii-key="YOUR_EMBED_API_KEY"
  data-emotii-default-language="English"
></script>`;

  const floaterExampleCode = `<!-- Default floater: bottom-right corner -->
<script
  src="https://emotii.ai/emotii.ai.embedded.js"
  data-emotii-key="YOUR_EMBED_API_KEY"
  data-emotii-default-language="English"
  data-emotii-theme="floater"
  data-emotii-position="bottom-right"
  data-emotii-offset-x="20"
  data-emotii-offset-y="30"
></script>`;

  const badgeExampleCode = `<!-- Badge: mounted to the right edge, centered -->
<script
  src="https://emotii.ai/emotii.ai.embedded.js"
  data-emotii-key="YOUR_EMBED_API_KEY"
  data-emotii-default-language="English"
  data-emotii-theme="badge"
  data-emotii-position="right"
></script>

<!-- Badge: top edge, aligned to the right side -->
<script
  src="https://emotii.ai/emotii.ai.embedded.js"
  data-emotii-key="YOUR_EMBED_API_KEY"
  data-emotii-theme="badge"
  data-emotii-position="top-right"
  data-emotii-offset="20%"
></script>`;

  const authRequestExample = `{
  "api_key": "YOUR_EMBED_API_KEY"
}`;

  const authResponseExample = `{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "expires_in": 3600
}`;

  const translateRequestExample = `{
  "texts": [
    {
      "id": 0,
      "text": "Welcome to our website"
    },
    {
      "id": 1,
      "text": "Learn more about our services"
    }
  ],
  "target_language": "French"
}`;

  const translateResponseExample = `{
  "status": "complete",
  "translations": [
    {
      "id": 0,
      "original": "Welcome to our website",
      "translated": "Bienvenue sur notre site Web"
    },
    {
      "id": 1,
      "original": "Learn more about our services",
      "translated": "En savoir plus sur nos services"
    }
  ]
}`;


  return (
    <Container>
      <HeaderRow>
        <Title>Website Plugin (Embed)</Title>
        <HeaderMeta>
          <Badge tone="brand" data-emotii-exclude>v1</Badge>
          <DividerDot />
          <Small data-emotii-exclude>Secure • JWT Auth • Domain Restricted</Small>
        </HeaderMeta>
      </HeaderRow>

      <PageContent>
        <Grid>
          {/* Installation */}
          <Card $span2>
            <CardHeader>
              <CardTitle>Quick Start</CardTitle>
              <Tabs>
                <Tab
                  aria-selected={activeInstallTab === "installation"}
                  onClick={() => setActiveInstallTab("installation")}
                >
                  Installation
                </Tab>
                <Tab
                  aria-selected={activeInstallTab === "attributes"}
                  onClick={() => setActiveInstallTab("attributes")}
                >
                  Attributes
                </Tab>
              </Tabs>
            </CardHeader>

            {activeInstallTab === "installation" && (
              <>
                <Note>
                  Add this script tag before the closing{" "}
                  <Mono data-emotii-exclude>&lt;/body&gt;</Mono> tag. The widget automatically translates all visible text on your page.
                </Note>
                <CodeBlock>
                  <Toolbar>
                    <LangPill data-emotii-exclude>HTML</LangPill>
                    <Spacer />
                    <CopyBtn
                      onClick={() => handleCopy("install", installationCode)}
                      data-copied={copied === "install" ? "true" : "false"}
                    >
                      <CopyIcon />
                      {copied === "install" ? "Copied" : "Copy"}
                    </CopyBtn>
                  </Toolbar>
                  <CodeArea role="region" aria-label="Installation Code" data-emotii-exclude>
                    <code>{installationCode}</code>
                  </CodeArea>
                </CodeBlock>
              </>
            )}

            {activeInstallTab === "attributes" && (
              <>
                <List>
                  <Item>
                    <Key data-emotii-exclude>data-emotii-key</Key>
                    <Desc>
                      <RequiredBadge>[Required]</RequiredBadge> Your embed API key from the Website Translator page.
                    </Desc>
                  </Item>
                  <Item>
                    <Key data-emotii-exclude>data-emotii-default-language</Key>
                    <Desc>
                      <OptionalBadge>[Optional]</OptionalBadge> The website's default language (e.g.,{" "}
                      <Mono data-emotii-exclude>"English"</Mono>). Prevents translation to the same language.
                    </Desc>
                  </Item>
                  <Item>
                    <Key data-emotii-exclude>data-emotii-theme</Key>
                    <Desc>
                      <OptionalBadge>[Optional]</OptionalBadge> Widget appearance:{" "}
                      <code data-emotii-exclude>"floater"</code> (default — corner bubble) or{" "}
                      <code data-emotii-exclude>"badge"</code> (compact tab mounted to a viewport edge).
                    </Desc>
                  </Item>
                  <Item>
                    <Key data-emotii-exclude>data-emotii-position</Key>
                    <Desc>
                      <OptionalBadge>[Optional]</OptionalBadge> Widget position. Floater:{" "}
                      <code data-emotii-exclude>"bottom-right"</code> (default),{" "}
                      <code data-emotii-exclude>"bottom-left"</code>,{" "}
                      <code data-emotii-exclude>"top-right"</code>,{" "}
                      <code data-emotii-exclude>"top-left"</code>. Badge edge:{" "}
                      <code data-emotii-exclude>"left"</code>,{" "}
                      <code data-emotii-exclude>"right"</code>,{" "}
                      <code data-emotii-exclude>"top"</code>,{" "}
                      <code data-emotii-exclude>"bottom"</code>. Badge corner-aligned:{" "}
                      <code data-emotii-exclude>"top-left"</code>,{" "}
                      <code data-emotii-exclude>"top-right"</code>,{" "}
                      <code data-emotii-exclude>"bottom-left"</code>,{" "}
                      <code data-emotii-exclude>"bottom-right"</code>.
                    </Desc>
                  </Item>
                  <Item>
                    <Key data-emotii-exclude>data-emotii-offset-x</Key>
                    <Desc>
                      <OptionalBadge>[Optional]</OptionalBadge> Horizontal pixel offset from the corner (floater theme only, e.g.,{" "}
                      <code data-emotii-exclude>"20"</code>).
                    </Desc>
                  </Item>
                  <Item>
                    <Key data-emotii-exclude>data-emotii-offset-y</Key>
                    <Desc>
                      <OptionalBadge>[Optional]</OptionalBadge> Vertical pixel offset from the corner (floater theme only, e.g.,{" "}
                      <code data-emotii-exclude>"30"</code>).
                    </Desc>
                  </Item>
                  <Item>
                    <Key data-emotii-exclude>data-emotii-offset</Key>
                    <Desc>
                      <OptionalBadge>[Optional]</OptionalBadge> Distance from the center or alignment anchor (badge theme only). Accepts pixels (e.g.,{" "}
                      <code data-emotii-exclude>"20"</code>) or a percentage (e.g.,{" "}
                      <code data-emotii-exclude>"25%"</code>).
                    </Desc>
                  </Item>
                </List>
              </>
            )}
          </Card>

          {/* Themes & Positioning */}
          <Card $span2>
            <CardHeader>
              <CardTitle>Themes &amp; Positioning</CardTitle>
            </CardHeader>
            <Note>
              <b>Floater</b> (default): a corner bubble that expands into a full language selector panel. Supports four corner positions with optional pixel offsets.
            </Note>
            <CodeBlock>
              <Toolbar>
                <LangPill data-emotii-exclude>HTML</LangPill>
                <Spacer />
                <CopyBtn
                  onClick={() => handleCopy("floater", floaterExampleCode)}
                  data-copied={copied === "floater" ? "true" : "false"}
                >
                  <CopyIcon />
                  {copied === "floater" ? "Copied" : "Copy"}
                </CopyBtn>
              </Toolbar>
              <CodeArea role="region" aria-label="Floater Theme Example" data-emotii-exclude>
                <code>{floaterExampleCode}</code>
              </CodeArea>
            </CodeBlock>
            <Note style={{ marginTop: "16px" }}>
              <b>Badge</b>: a compact tab mounted to any edge of the viewport. Opens a panel on tap. After a language is selected, the trigger displays the country flag and language code.
            </Note>
            <CodeBlock>
              <Toolbar>
                <LangPill data-emotii-exclude>HTML</LangPill>
                <Spacer />
                <CopyBtn
                  onClick={() => handleCopy("badge", badgeExampleCode)}
                  data-copied={copied === "badge" ? "true" : "false"}
                >
                  <CopyIcon />
                  {copied === "badge" ? "Copied" : "Copy"}
                </CopyBtn>
              </Toolbar>
              <CodeArea role="region" aria-label="Badge Theme Example" data-emotii-exclude>
                <code>{badgeExampleCode}</code>
              </CodeArea>
            </CodeBlock>
          </Card>

          {/* Auth Examples */}
          <Card $span2>
            <CardHeader>
              <CardTitle>Authentication Examples</CardTitle>
              <Tabs>
                <Tab
                  aria-selected={activeAuthTab === "authRequest"}
                  onClick={() => setActiveAuthTab("authRequest")}
                >
                  Request
                </Tab>
                <Tab
                  aria-selected={activeAuthTab === "authResponse"}
                  onClick={() => setActiveAuthTab("authResponse")}
                >
                  Response
                </Tab>
              </Tabs>
            </CardHeader>

            <Note style={{ marginBottom: "12px" }}>
              <b>Authentication Flow:</b> The widget automatically handles authentication by calling the auth endpoint to get a <Mono data-emotii-exclude>JWT</Mono> token. The backend validates your API key and origin domain against your configured allowed domains. Tokens expire after 1 hour.
            </Note>

            {activeAuthTab === "authRequest" && (
              <CodeBlock>
                <Toolbar>
                  <LangPill data-emotii-exclude>JSON</LangPill>
                  <Spacer />
                  <CopyBtn
                    onClick={() => handleCopy("authReq", authRequestExample)}
                    data-copied={copied === "authReq" ? "true" : "false"}
                  >
                    <CopyIcon />
                    {copied === "authReq" ? "Copied" : "Copy"}
                  </CopyBtn>
                </Toolbar>
                <CodeArea role="region" aria-label="Auth Request" data-emotii-exclude>
                  <code>
                    {`{
  "`}
                    <Red>api_key</Red>
                    {`": "`}
                    <Purple>YOUR_EMBED_API_KEY</Purple>
                    {`"
}`}
                  </code>
                </CodeArea>
              </CodeBlock>
            )}

            {activeAuthTab === "authResponse" && (
              <CodeBlock>
                <Toolbar>
                  <LangPill data-emotii-exclude>JSON</LangPill>
                  <Spacer />
                  <CopyBtn
                    onClick={() => handleCopy("authRes", authResponseExample)}
                    data-copied={copied === "authRes" ? "true" : "false"}
                  >
                    <CopyIcon />
                    {copied === "authRes" ? "Copied" : "Copy"}
                  </CopyBtn>
                </Toolbar>
                <CodeArea role="region" aria-label="Auth Response" data-emotii-exclude>
                  <code>
                    {`{
  "`}
                    <Red>token</Red>
                    {`": "`}
                    <Purple>eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...</Purple>
                    {`",
  "`}
                    <Red>expires_in</Red>
                    {`": `}
                    <Purple>3600</Purple>
                    {`
}`}
                  </code>
                </CodeArea>
              </CodeBlock>
            )}

          </Card>

          {/* Translation Examples */}
          <Card $span2>
            <CardHeader>
              <CardTitle>Translation Examples</CardTitle>
              <Tabs>
                <Tab
                  aria-selected={activeTranslateTab === "translateRequest"}
                  onClick={() => setActiveTranslateTab("translateRequest")}
                >
                  Request
                </Tab>
                <Tab
                  aria-selected={activeTranslateTab === "translateResponse"}
                  onClick={() => setActiveTranslateTab("translateResponse")}
                >
                  Response
                </Tab>
              </Tabs>
            </CardHeader>

            <Note style={{ marginBottom: "12px" }}>
              <b>Translation Endpoint:</b> The widget sends batch translation requests. The service returns immediately with cached results and a <Mono data-emotii-exclude>job_id</Mono> for polling any remaining translations.
            </Note>

            {activeTranslateTab === "translateRequest" && (
              <CodeBlock>
                <Toolbar>
                  <LangPill data-emotii-exclude>JSON</LangPill>
                  <Spacer />
                  <CopyBtn
                    onClick={() => handleCopy("transReq", translateRequestExample)}
                    data-copied={copied === "transReq" ? "true" : "false"}
                  >
                    <CopyIcon />
                    {copied === "transReq" ? "Copied" : "Copy"}
                  </CopyBtn>
                </Toolbar>
                <CodeArea role="region" aria-label="Translate Request" data-emotii-exclude>
                  <code>{translateRequestExample}</code>
                </CodeArea>
              </CodeBlock>
            )}

            {activeTranslateTab === "translateResponse" && (
              <CodeBlock>
                <Toolbar>
                  <LangPill data-emotii-exclude>JSON</LangPill>
                  <Spacer />
                  <CopyBtn
                    onClick={() => handleCopy("transRes", translateResponseExample)}
                    data-copied={copied === "transRes" ? "true" : "false"}
                  >
                    <CopyIcon />
                    {copied === "transRes" ? "Copied" : "Copy"}
                  </CopyBtn>
                </Toolbar>
                <CodeArea role="region" aria-label="Translate Response" data-emotii-exclude>
                  <code>{translateResponseExample}</code>
                </CodeArea>
              </CodeBlock>
            )}

          </Card>

          {/* Exclude Content */}
          <Card $span2>
            <CardHeader>
              <CardTitle>Excluding Content from Translation</CardTitle>
            </CardHeader>
            <Note>
              <b>Skip Translation:</b> Use the <Mono data-emotii-exclude>data-emotii-exclude</Mono> attribute to prevent specific elements and their children from being translated. This is useful for brand names, code snippets, proper nouns, or technical content that should remain in the original language.
            </Note>

            <CodeBlock>
              <Toolbar>
                <LangPill data-emotii-exclude>HTML</LangPill>
                <Spacer />
                <CopyBtn
                  onClick={() => handleCopy("exclude", `<!-- Exclude a specific element -->\n<div data-emotii-exclude>\n  This text will not be translated\n</div>\n\n<!-- Brand name example -->\n<h1>Welcome to <span data-emotii-exclude>Emotii</span></h1>\n\n<!-- Code block example -->\n<pre data-emotii-exclude>\n  <code>const greeting = "Hello World";</code>\n</pre>\n\n<!-- Technical content -->\n<div class="api-docs">\n  <p>Connect to our API at:</p>\n  <code data-emotii-exclude>https://api.example.com/v1</code>\n</div>`)}
                  data-copied={copied === "exclude" ? "true" : "false"}
                >
                  <CopyIcon />
                  {copied === "exclude" ? "Copied" : "Copy"}
                </CopyBtn>
              </Toolbar>
              <CodeArea role="region" aria-label="Exclude Content Example" data-emotii-exclude>
                <code>{`<!-- Exclude a specific element -->
<div data-emotii-exclude>
  This text will not be translated
</div>

<!-- Brand name example -->
<h1>Welcome to <span data-emotii-exclude>Emotii</span></h1>

<!-- Code block example -->
<pre data-emotii-exclude>
  <code>const greeting = "Hello World";</code>
</pre>

<!-- Technical content -->
<div class="api-docs">
  <p>Connect to our API at:</p>
  <code data-emotii-exclude>https://api.example.com/v1</code>
</div>`}</code>
              </CodeArea>
            </CodeBlock>

            <Note style={{ marginTop: "16px" }}>
              <b>Important:</b> The <Mono data-emotii-exclude>data-emotii-exclude</Mono> attribute applies to the entire element and all its descendants. All text within the marked element will be skipped during translation.
            </Note>

            <List style={{ marginTop: "12px" }}>
              <Item>
                <Key>Brand Names</Key>
                <Desc>
                  Wrap brand names or product names in elements with <Mono data-emotii-exclude>data-emotii-exclude</Mono> to keep them consistent across languages.
                </Desc>
              </Item>
              <Item>
                <Key>Code Snippets</Key>
                <Desc>
                  Apply to <Mono data-emotii-exclude>&lt;pre&gt;</Mono>, <Mono data-emotii-exclude>&lt;code&gt;</Mono>, or other technical content that should not be translated.
                </Desc>
              </Item>
              <Item>
                <Key>URLs & Emails</Key>
                <Desc>
                  Use on elements containing URLs, email addresses, or other technical identifiers.
                </Desc>
              </Item>
              <Item>
                <Key>Proper Nouns</Key>
                <Desc>
                  Apply to names of people, places, or organizations that should remain unchanged.
                </Desc>
              </Item>
            </List>
          </Card>

          {/* Domain Security */}
          <Card $span2>
            <CardHeader>
              <CardTitle>Domain Security</CardTitle>
            </CardHeader>
            <Note>
              <b>Domain Whitelisting:</b> Your embed API keys are restricted to specific domains for security. The authentication endpoint validates the <Mono data-emotii-exclude>Origin</Mono> header against your configured allowed domains.
            </Note>
            <List>
              <Item>
                <Key>Allowed Domains</Key>
                <Desc>
                  Configure allowed domains in the Website Translator page. Each key can have multiple domains (e.g., <Mono data-emotii-exclude>example.com</Mono>, <Mono data-emotii-exclude>www.example.com</Mono>).
                </Desc>
              </Item>
              <Item>
                <Key>Origin Header</Key>
                <Desc>
                  The browser automatically sends the <Mono data-emotii-exclude>Origin</Mono> header. Manual API calls must include it for authentication to succeed.
                </Desc>
              </Item>
              <Item>
                <Key>Subdomain Support</Key>
                <Desc>
                  Each subdomain must be explicitly allowed. <Mono data-emotii-exclude>example.com</Mono> does not automatically allow <Mono data-emotii-exclude>www.example.com</Mono>.
                </Desc>
              </Item>
            </List>
          </Card>

          {/* Error Handling */}
          <Card $span2>
            <CardHeader>
              <CardTitle>Error Handling</CardTitle>
            </CardHeader>
            <ErrorList>
              <ErrorRow>
                <ErrCode $tone="warn" data-emotii-exclude>400</ErrCode>
                <ErrText>
                  Bad Request — invalid JSON or missing required fields.
                </ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode $tone="danger" data-emotii-exclude>401</ErrCode>
                <ErrText>
                  Unauthorized — invalid API key or <Mono data-emotii-exclude>JWT</Mono> token.
                </ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode $tone="danger" data-emotii-exclude>403</ErrCode>
                <ErrText>
                  Forbidden — origin domain not in allowed list for this API key.
                </ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode $tone="neutral" data-emotii-exclude>500</ErrCode>
                <ErrText>
                  Internal Server Error — something went wrong server-side.
                </ErrText>
              </ErrorRow>
            </ErrorList>
          </Card>
        </Grid>
      </PageContent>
    </Container>
  );
};

export default EmbedDocumentationPage;

/* ---------------- Styles (identical to DocumentationPage) ---------------- */

const Container = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
  width: 100%;
  background: #fafafa;
  overflow: hidden;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Title = styled.h2`
  color: ${lightThemeColors.darkColor};
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  margin: 0;
`;

const HeaderMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const Small = styled.span`
  font-size: 12px;
  color: rgba(56, 56, 58, 0.7);
`;

const DividerDot = styled.span`
  width: 4px;
  height: 4px;
  background: rgba(56, 56, 58, 0.35);
  border-radius: 50%;
  display: inline-block;
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${(props) =>
    props.tone === "brand"
      ? lightThemeColors.primaryColor
      : "rgba(56,56,58,0.1)"};
  color: ${(props) =>
    props.tone === "brand" ? "#fff" : lightThemeColors.darkColor};
`;

const PageContent = styled.div`
  overflow: auto;
  padding: 24px;
`;

const Grid = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(12, 1fr);
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  border-radius: 14px;
  padding: 18px;
  grid-column: ${(props) => (props.$span2 ? "span 12" : "span 6")};

  @media (max-width: 1024px) {
    grid-column: 1 / -1;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const CardTitle = styled.h3`
  color: ${lightThemeColors.darkColor};
  font-size: 16px;
  font-weight: 800;
  margin: 0;
`;

const Note = styled.p`
  color: rgba(56, 56, 58, 0.75);
  font-size: 13px;
  line-height: 1.6;
  margin: 4px 0 14px;
`;

const List = styled.div`
  display: grid;
  gap: 10px;
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 12px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Key = styled.div`
  color: ${lightThemeColors.darkColor};
  font-weight: 700;
  font-size: 13px;
  background: #f5f5f5;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  width: fit-content;
`;

const Desc = styled.div`
  color: rgba(56, 56, 58, 0.9);
  font-size: 13px;
  line-height: 1.6;
`;

const OptionalBadge = styled.span`
  color: #059669;
  font-weight: 600;
`;

const RequiredBadge = styled.span`
  color: #dc2626;
  font-weight: 600;
`;

const Tabs = styled.div`
  display: inline-flex;
  background: rgba(56, 56, 58, 0.06);
  border-radius: 10px;
  padding: 4px;
  gap: 4px;
`;

const Tab = styled.button`
  border: none;
  background: transparent;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 800;
  color: ${lightThemeColors.darkColor};
  cursor: pointer;
  transition: all 0.15s ease;

  &[aria-selected="true"] {
    background: #fff;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
  }
`;

const CodeBlock = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(34, 39, 46, 1);
  color: #fff;
  border-radius: 12px;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
`;

const LangPill = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
  background: rgba(72, 165, 240, 0.18);
  color: rgba(72, 165, 240, 1);
  border: 1px solid rgba(72, 165, 240, 0.35);
  padding: 4px 8px;
  border-radius: 999px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const CopyBtn = styled.button`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &[data-copied="true"] {
    background: rgba(72, 165, 240, 0.2);
    border-color: rgba(72, 165, 240, 0.5);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const CodeArea = styled.pre`
  font-family: "Menlo", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 12.5px;
  line-height: 1.45;
  padding: 14px;
  margin: 0;
  max-width: 100%;
  overflow: auto;
  white-space: pre;
  word-break: normal;

  code {
    font-family: inherit;
  }
`;

const ErrorList = styled.div`
  display: grid;
  gap: 10px;
`;

const ErrorRow = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr;
  gap: 12px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const ErrCode = styled.div`
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.3px;
  border-radius: 6px;
  padding: 6px 10px;
  text-align: center;
  width: fit-content;

  color: ${(props) =>
    props.$tone === "danger"
      ? "#991b1b"
      : props.$tone === "warn"
      ? "#991b1b"
      : "#991b1b"};
  background: ${(props) =>
    props.$tone === "danger"
      ? "#fee2e2"
      : props.$tone === "warn"
      ? "#fee2e2"
      : "#fee2e2"};
  border: 1px solid
    ${(props) =>
      props.$tone === "danger"
        ? "#fecaca"
        : props.$tone === "warn"
        ? "#fecaca"
        : "#fecaca"};
`;

const ErrText = styled.div`
  color: rgba(56, 56, 58, 0.9);
  font-size: 13px;
`;

const Mono = styled.code`
  font-family: "Menlo", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 0.95em;
`;

const Red = styled.span`
  color: rgba(252, 115, 76);
  font-family: Menlo, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
`;

const Purple = styled.span`
  color: rgba(72, 165, 240);
  font-family: Menlo, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
`;

const EndpointRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const EndpointLabel = styled.div`
  color: #059669;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  background: #d1fae5;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #a7f3d0;
`;

const EndpointValue = styled.div`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 600;
  word-break: break-all;
  font-family: "Menlo", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
`;

const HeadersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FirstHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SecondHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: 100px;
`;

const HeadersLabel = styled.div`
  color: #d97706;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: #fed7aa;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #fdba74;
  width: fit-content;
`;

const HeaderKey = styled.div`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 600;
  background: #f5f5f5;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  width: fit-content;
`;

const HeaderValue = styled.div`
  color: #000;
  font-size: 14px;
  font-weight: 400;
`;

const CopyEndpointBtn = styled.button`
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  color: #374151;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: #e5e7eb;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const CopyIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3.33333 10H2.66667C2.31304 10 1.97391 9.85952 1.72386 9.60947C1.47381 9.35943 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31304 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31304 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35943 1.47381 9.60947 1.72386C9.85952 1.97391 10 2.31304 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
